/**
 * ScanLog v4 — HMAC request signing.
 *
 * v4.1.5 R2 — CCQ-292 R-H1 (replay) + R-M1 (canonical-JSON alignment).
 *
 * Matches Apps Script side (`google-apps-script.gs`
 *   > verifyPostSignature_ / verifyGetSignature_ / canonicalJsonForSig_)
 * byte-for-byte. Any deviation here breaks sync silently.
 *
 * Algorithm:
 *   POST: sig = hex(HMAC-SHA256(secret, "POST\n" + ts + "\n" + nonce + "\n" + canonical(body)))
 *   GET:  sig = hex(HMAC-SHA256(secret, "GET\n" + action + "\n" + ts + "\n" + nonce + "\n" + fingerprint))
 *
 * v4.1.5 R4 R3-C1: POST blob now binds `nonce` alongside `ts`. Without this
 * binding, an attacker who captures a signed POST could swap the nonce
 * value while keeping `ts` + `canonical(body)` identical — the HMAC would
 * still verify, and only the consumeNonce_ cache would block replay. That
 * cache has a race (see R3-H1) plus a 10-min TTL — this makes the replay
 * window a true cryptographic block, not a cache-TTL block.
 *
 * `canonical(body)` = JSON-stringify of the body with:
 *   - TOP-LEVEL {ts, nonce, sig} keys stripped (nested objects keep them)
 *   - all object keys sorted at every depth
 *   - array order preserved
 *
 * Three layered replay defenses: 5-minute ts window, HMAC, and
 * per-request nonce dedup server-side (CacheService TTL 10 min).
 */

export interface CanonicalJsonInput {
	[key: string]: unknown;
}

/**
 * Deterministic JSON used as the signed payload.
 *
 * Strips {ts, nonce, sig} AT TOP LEVEL ONLY — nested objects keep every
 * key. This matches the server implementation exactly; a prior version
 * of this function recursively stripped those keys and would have
 * silently broken auth the moment an event payload acquired a nested
 * `ts` field.
 */
export function canonicalJsonForSig(body: CanonicalJsonInput): string {
	const clone: Record<string, unknown> = {};
	for (const k of Object.keys(body)) {
		if (k === 'ts' || k === 'nonce' || k === 'sig') continue;
		clone[k] = body[k];
	}
	return stringifySorted(clone);
}

function stringifySorted(v: unknown): string {
	if (v === null || v === undefined) return 'null';
	if (typeof v === 'number' || typeof v === 'boolean') return JSON.stringify(v);
	if (typeof v === 'string') return JSON.stringify(v);
	if (Array.isArray(v)) {
		return '[' + v.map(stringifySorted).join(',') + ']';
	}
	if (typeof v === 'object') {
		const obj = v as Record<string, unknown>;
		// Nested objects: keep all keys, sorted. No ts/nonce/sig stripping.
		const keys = Object.keys(obj).sort();
		return (
			'{' +
			keys
				.map((k) => JSON.stringify(k) + ':' + stringifySorted(obj[k]))
				.join(',') +
			'}'
		);
	}
	return 'null';
}

/** Generate a UUIDv4 nonce (browser-native). */
function freshNonce(): string {
	return crypto.randomUUID();
}

/** Hex-encoded HMAC-SHA256 using Web Crypto. */
export async function hmacSha256Hex(secret: string, message: string): Promise<string> {
	const enc = new TextEncoder();
	const key = await crypto.subtle.importKey(
		'raw',
		enc.encode(secret),
		{ name: 'HMAC', hash: 'SHA-256' },
		false,
		['sign']
	);
	const buf = await crypto.subtle.sign('HMAC', key, enc.encode(message));
	const bytes = new Uint8Array(buf);
	let hex = '';
	for (let i = 0; i < bytes.length; i++) {
		const b = bytes[i]!;
		hex += (b < 16 ? '0' : '') + b.toString(16);
	}
	return hex;
}

/**
 * Sign a POST body. Adds `ts`, `nonce`, `sig` fields; does not mutate input.
 * R-H1: nonce makes each request single-use even inside the ts window.
 */
export async function signPostBody<T extends CanonicalJsonInput>(
	secret: string,
	body: T
): Promise<T & { ts: string; nonce: string; sig: string }> {
	const ts = new Date().toISOString();
	const nonce = freshNonce();
	const withTsNonce = { ...body, ts, nonce };
	const canonical = canonicalJsonForSig(withTsNonce);
	// R3-C1: bind nonce into MAC blob (symmetric with GET path).
	const sig = await hmacSha256Hex(secret, 'POST\n' + ts + '\n' + nonce + '\n' + canonical);
	return { ...withTsNonce, sig };
}

/**
 * Sign a GET request. Returns {ts, nonce, sig} to append as query params.
 * `fingerprint` is a caller-supplied canonical string describing the
 * remaining query params (e.g. 'since=2026-04-19T00:00:00.000Z').
 *
 * R-H1: nonce becomes part of the signed blob so it can't be swapped.
 */
export async function signGetRequest(
	secret: string,
	action: string,
	fingerprint: string
): Promise<{ ts: string; nonce: string; sig: string }> {
	const ts = new Date().toISOString();
	const nonce = freshNonce();
	const sig = await hmacSha256Hex(
		secret,
		'GET\n' + action + '\n' + ts + '\n' + nonce + '\n' + fingerprint
	);
	return { ts, nonce, sig };
}

/** Generate a 256-bit base64 secret suitable for HMAC_SECRET. */
export function generateHmacSecret(): string {
	const bytes = new Uint8Array(32);
	crypto.getRandomValues(bytes);
	let bin = '';
	for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
	return btoa(bin);
}
